/** Piano movement and phrase search. Voices keep their vertical order and
 * their hand; a texture change cannot match a right-hand tone to the bass.
 * See docs/piano-voicing-review.md for musical sources and policy choices. */
export interface PianoVoiceLayout {
  midis: number[];
  hands?: { left: number[]; right: number[] };
}

const leapCost = (distance: number) => distance + Math.max(0, distance - 4) ** 2;

/** Match every voice in the smaller texture, in order. Only the excess voices
 * may enter/leave; equal-size chords can never make a leap cheap by deleting
 * and reintroducing a voice. The top is paired separately below. */
function innerMovement(a: number[], b: number[], cost: (n: number) => number): number {
  if (a.length > b.length) return innerMovement(b, a, cost);
  let previous = b.map((_, i) => i * 3);
  previous.push(b.length * 3);
  for (let i = 1; i <= a.length; i += 1) {
    const next = Array<number>(b.length + 1).fill(Infinity);
    for (let j = i; j <= b.length; j += 1) {
      next[j] = Math.min(previous[j - 1] + cost(Math.abs(a[i - 1] - b[j - 1])), next[j - 1] + 3);
    }
    previous = next;
  }
  return previous[b.length];
}

function handMovement(a: number[], b: number[], expressive: boolean, pinTop = true): number {
  // Bass roots regularly move a fourth/fifth. Penalize larger LH leaps,
  // while the upper line retains its tighter stepwise preference.
  const cost = expressive
    ? pinTop
      ? leapCost
      : (n: number) => n + Math.max(0, n - 7) ** 2
    : (n: number) => n;
  if (!a.length || !b.length) return 3 * Math.max(a.length, b.length);
  const outer = cost(Math.abs(a[pinTop ? a.length - 1 : 0] - b[pinTop ? b.length - 1 : 0]));
  return (
    (expressive ? 1.5 : 1) * outer +
    innerMovement(pinTop ? a.slice(0, -1) : a.slice(1), pinTop ? b.slice(0, -1) : b.slice(1), cost)
  );
}

/** Raw movement for reporting, or leap-sensitive movement for choosing a line.
 * LH root motion has less influence than the upper line, but is never free. */
export function pianoMovement(
  a: PianoVoiceLayout,
  b: PianoVoiceLayout,
  expressive = false,
): number {
  if (a.hands && b.hands) {
    return (
      (expressive ? 0.5 : 1) * handMovement(a.hands.left, b.hands.left, expressive, false) +
      handMovement(a.hands.right, b.hands.right, expressive)
    );
  }
  return handMovement(a.midis, b.midis, expressive);
}

/** Exact dynamic programming over the supplied candidates. No beam or greedy
 * pruning: the opening inversion can leave room for later resolutions. Node
 * costs are evaluated once, edges once per adjacent candidate pair. */
export function pianoPhrase<T>(
  layers: T[][],
  localCost: (candidate: T, index: number) => number,
  transitionCost: (a: T, b: T) => number,
): T[] | null {
  if (!layers.length || layers.some((layer) => !layer.length)) return null;
  const parents: number[][] = [layers[0].map(() => -1)];
  let costs = layers[0].map((candidate) => localCost(candidate, 0));
  for (let index = 1; index < layers.length; index += 1) {
    const parent: number[] = [];
    const next = layers[index].map((candidate) => {
      let best = Infinity;
      let from = 0;
      for (let p = 0; p < layers[index - 1].length; p += 1) {
        const cost = costs[p] + transitionCost(layers[index - 1][p], candidate);
        if (cost < best) {
          best = cost;
          from = p;
        }
      }
      parent.push(from);
      return best + localCost(candidate, index);
    });
    parents.push(parent);
    costs = next;
  }
  let chosen = costs.reduce((best, cost, i) => (cost < costs[best] ? i : best), 0);
  const out: T[] = [];
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    out.push(layers[i][chosen]);
    chosen = parents[i][chosen];
  }
  return out.reverse();
}
